'use client';
import React, { useMemo, useRef, useState } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Drawer, DrawerContent, DrawerTitle } from '@/components/ui/drawer';
import { cn } from '@/utils/classnames';
import { useTranslations } from 'next-intl';
import '../form/edit.css';
import { PhoneNumberInput, VerificationCodeInput } from '../form/login-input';
import { Button } from '@/components/ui/button';
import { startCountdown } from '../../util';
import { usePhoneBind, useBindSendCode } from '../../services/user-auth';
import toast from 'react-hot-toast';
import { defaultPhoneCountry, type CountryCode } from '../form/country-codes';
import '@/features/user-center/styles/btn.css';
import { z } from 'zod';
import ModalTitle from '../modal-title';
import { useAuthError } from '../../hooks/useAuthError';
import { BlackStatus, useBlackCheck } from '@/features/auth/services/black-check';
import { useDeviceID } from '@/components/providers/device-id-provider';
import { VerifyMethod } from '../../constants';
import useCodeStatus from '../../hooks/useCfTokenCodeStatus';
import { track } from '@/utils/track';
import useUserInfo from '../../hooks/useUserInfo';
import FocusFixCom from '@/features/user-center/components/focus-fix-com/index';

interface ModalPhoneEditProps {
  /** Modal title */
  title?: string;
  /** Custom styles */
  className?: string;
  /** Custom overlayClassName */
  overlayClassName?: string;
  /** Callback function when modal closes */
  onClose?: () => void;
  /** Callback function when edit succeeds */
  onSuccess?: () => void;
  /** Cancel button text */
  cancelText?: string;
  /** 是否有跳过按钮 */
  isSkip?: boolean;
}

const ModalPhoneEdit = NiceModal.create<ModalPhoneEditProps>(({
  title: _title = '',
  className,
  overlayClassName,
  onClose,
  onSuccess,
  cancelText,
}) => {
  const modal = useModal();
  const t = useTranslations('personal');
  const authT = useTranslations('auth');
  // Form states
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [countryCode, setCountryCode] = useState(defaultPhoneCountry.code);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);
  const [hasSentCode, setHasSentCode] = useState(false);
  const { freshUserInfo } = useUserInfo();
  const enableGetRef = useRef(true);
  const [enableGet, setEnableGet] = useState(true);
  const canSubmit = useMemo(() => {
    return phone.length && verificationCode.length;
  }, [phone, verificationCode]);

  // Zod schema for form validation
  const phoneEditSchema = z.object({
    phone: z.string()
      .min(1, { message: t('form.validation.pleaseEnterPhoneNumber') })
      .regex(/^[6-9]\d{9}$/, { message: t('form.validation.pleaseEnterValidPhoneNumber') }),
    verificationCode: z.string()
      .min(1, { message: t('form.validation.pleaseEnterVerificationCode') })
      .regex(/^\d{6}$/, { message: t('form.validation.pleaseEnter4To6DigitVerificationCode') }),
    phoneCountryCode: z.string()
  });

  const handleClose = () => {
    track('account_bind_page_button_click', {
      bind_type: 'mobile',
      button_name: cancelText === t('common.skip') ? 'skip' : 'cancel',
    });
    onClose?.();
    modal.resolve(undefined);
    modal.hide();
    setPhoneError('');
    setVerificationError('');
    setResendCountdown(0);
    setHasSentCode(false);
  };

  const handleSuccess = (token) => {
    toast(t('common.bindingSuccessful'));
    freshUserInfo();
    onSuccess?.();
    modal.resolve({
      token
    });
    modal.hide();
  };

  const { authErrorCodeMap } = useAuthError();
  const { mutate: phoneBindMutation, isPending: isBinding } = usePhoneBind({
    mutationConfig: {
      onSuccess: (result) => {
        if (!result.success) {
          if (result.code === 102010) {
            setVerificationError(authT('errors.invalidCode'));
            return;
          }
          const error = authErrorCodeMap[result.code || 0] || result.msg;
          setPhoneError(error || '  ');
          return;
        }
        handleSuccess(result.token);
      },
      onError: (error) => {
        console.error('error:', error);
      }
    }
  });

  const { checkCodeStatus, getCfToken } = useCodeStatus();
  const { mutate: sendCodeMutation } = useBindSendCode({
    mutationConfig: {
      onSuccess: (result) => {
        if (result.code) {
          const error = authErrorCodeMap[result.code || 0] || result.msg;
          setVerificationError(error || '  ');
          track('get_verification_code_result', {
            business_model: 'bind_mobile',
            is_success: false,
            fail_reason: result.msg,
          });
          resetBtn(true);
          return;
        }
        const codeRes = checkCodeStatus(result.codeStatus, 'bind_mobile');
        if (codeRes) {
          setPhoneError('');
          setVerificationError('');
          setHasSentCode(true);
          startCountdown(60, setResendCountdown);
          toast(t('common.verificationCodesent'));
        }
        resetBtn(true);
      },
      onError: () => {
        resetBtn(true);
      }
    }
  });

  const validateForm = async () => {
    const formData = {
      phone: phone.trim(),
      verificationCode: verificationCode.trim(),
      phoneCountryCode: countryCode
    };

    try {
      const validatedData = await phoneEditSchema.parseAsync(formData);
      setPhoneError('');
      setVerificationError('');
      return { success: true, data: validatedData };
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors = error.flatten().fieldErrors;
        const phoneErrors = (fieldErrors as { phone?: string[]; }).phone || [];
        const verificationErrors = (fieldErrors as { verificationCode?: string[]; }).verificationCode || [];

        if (phoneErrors.length > 0) {
          setPhoneError(t(phoneErrors[0] === 'Invalid phone number format' ? 'form.validation.pleaseEnterValidPhoneNumber' : 'form.validation.pleaseEnterPhoneNumber'));
        } else {
          setPhoneError('');
        }

        if (verificationErrors.length > 0) {
          setVerificationError(verificationErrors[0] === 'Invalid verification code format' ? t('form.validation.pleaseEnter4To6DigitVerificationCode') : authT('verification.title'));
        } else {
          setVerificationError('');
        }

        return { success: false, error };
      }
      return { success: false, error: new Error('Validation failed') };
    }
  };

  const deviceId = useDeviceID() || '';
  const { mutate: runBlackCheck } = useBlackCheck({
    mutationConfig: {}
  });

  const resetBtn = (status: boolean) => {
    enableGetRef.current = status;
    setEnableGet(status);
  };

  const handleSendCode = () => {
    if (!enableGetRef.current) {
      return;
    }
    resetBtn(false);

    const result = phoneEditSchema.safeParse({
      phone: phone.trim(),
      verificationCode: '',
      phoneCountryCode: countryCode
    });

    if (!result.success) {
      const fieldErrors = result.error.flatten().fieldErrors;
      const phoneErrors = fieldErrors.phone || [];
      if (phoneErrors.length > 0) {
        setPhoneError(t(phoneErrors[0] === 'Invalid phone number format' ? 'form.validation.pleaseEnterValidPhoneNumber' : 'form.validation.pleaseEnterPhoneNumber'));
        resetBtn(true);
        return;
      }
    }

    setPhoneError('');

    runBlackCheck({ params: { account: phone, phoneCountryCode: countryCode, loginType: VerifyMethod.Phone, deviceId } }, {
      onSuccess: async (result) => {
        if (result.blackStatus === BlackStatus.BLACK_STATUS_YES) {
          setPhoneError(authT('errors.contactSupport'));
          resetBtn(true);
          return;
        } else if (result.blackStatus === BlackStatus.BLACK_STATUS_NO) {
          const cfToken = await getCfToken();
          sendCodeMutation({ params: { phone, phoneCountryCode: countryCode, cfToken } });
          track('get_verification_code', {
            business_model: 'bind_mobile',
          });
          return;
        }
        resetBtn(true);
      },
      onError: () => {
        resetBtn(true);
      }
    });
  };

  const handleSubmit = async () => {
    track('account_bind_page_button_click', {
      bind_type: 'mobile',
      button_name: 'link',
    });
    const result = await validateForm();
    if (!result.success || !result.data) {
      console.log('Form validation failed:', result.error);
      return;
    }

    const { phone, verificationCode: code, phoneCountryCode } = result.data;
    phoneBindMutation({ params: { phone, code, phoneCountryCode } });
  };

  return (
    <Drawer open={modal.visible} onOpenChange={(open) => !open && handleClose()}>
      <DrawerTitle></DrawerTitle>
      <DrawerContent className={cn("bg-surface", className)} overlayClassName={overlayClassName}>
        <ModalTitle className='mb-5' title={t('account.bindMobilePhone')} showBack={false} onClose={handleClose} />
        <div className='flex flex-col items-center px-[1.6875rem] mb-5'>
          <p className="font-medium text-xss text-tertiary mb-[2.0625rem]">{t('account.bindMobileDescription')}</p>
          <div className="flex-1 w-full">
            <PhoneNumberInput
              phone={phone}
              onPhoneChange={setPhone}
              selectedCountryCode={countryCode as unknown as CountryCode}
              onCountryCodeChange={(countryCode) => {
                setCountryCode(countryCode.code);
              }}
              error={phoneError}
              className={`${phoneError ? 'mb-10' : 'mb-6'} h-[45px]`}
            />
            <VerificationCodeInput
              code={verificationCode}
              onCodeChange={setVerificationCode}
              onResend={handleSendCode}
              countdown={resendCountdown}
              hasSentCode={hasSentCode}
              error={verificationError}
              className='h-[45px]'
              disabled={!enableGet}
            />
          </div>

          <div className="mt-9 flex items-center w-full">
            <Button className='flex-1 default-btn large-btn' onClick={handleClose}>
              {cancelText || t('common.cancel')}
            </Button>
            <Button disabled={!canSubmit || isBinding} loading={isBinding} className='flex-1 primary-btn large-btn ml-2.5' onClick={handleSubmit}>
              {isBinding ? authT('binding.linking') : authT('binding.title')}
            </Button>
          </div>
          <FocusFixCom />
        </div>
      </DrawerContent>
    </Drawer>
  );
});

ModalPhoneEdit.displayName = 'ModalPhoneEdit';

export const showModalPhoneEdit = (
  props: ModalPhoneEditProps = {}
): Promise<{ token: string; } | undefined> => {
  track('account_bind_page_view', { bind_type: 'Mobile' });
  return NiceModal.show(ModalPhoneEdit, props);
};

export default ModalPhoneEdit;
